import { Box, Stack, Typography, useTheme } from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import {
  useReassistBilling,
  useReassistRequests,
} from "../api/useReassistRequests";
import {
  REASSIST_ARCHIVE_PATH,
  REASSIST_CONTENT_MAX_WIDTH,
  REASSIST_REQUEST_PATH,
} from "../config";
import { useReassistArchiveEnabled } from "../hooks/useReassistArchiveEnabled";
import { useRequestListView } from "../hooks/useRequestListView";
import { type ReassistRequestSummary } from "../types";
import { formatDate } from "../utils/format";
import { matchesQuery } from "../utils/search";
import { sectionSx, TEXT } from "../utils/tokens";
import { PurchasedDocumentsSection } from "@/features/reassistSale/components/PurchasedDocumentsSection";
import { ReassistBillingTable } from "./ReassistBillingTable";
import {
  ReassistArchiveButton,
  ReassistButton,
  ReassistRequestButton,
} from "./ReassistButton";
import { ReassistPageHeader } from "./ReassistPageHeader";
import { ReassistRequestRow } from "./ReassistRequestRow";
import { ReassistToolbar } from "./ReassistToolbar";
import { RequestTable } from "./RequestTable";
import { EmptyBlock, LoadFailed, LoadingCards } from "./StateBlocks";

const REQUESTS_FAILED =
  "ご依頼の一覧を読み込めませんでした。少し待ってから再読み込みしてください。";

/** セクション見出し（18px／700）と、その右に 14px の件数。 */
const SectionTitle: React.FC<{
  children: string;
  count?: number;
}> = ({ children, count }) => (
  <Stack direction="row" gap={1} sx={{ alignItems: "baseline", mb: 2 }}>
    <Typography component="h2" sx={{ ...TEXT.section, color: "text.primary" }}>
      {children}
    </Typography>
    {count !== undefined && (
      <Typography
        component="span"
        sx={{
          ...TEXT.body,
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {`${count}件`}
      </Typography>
    )}
  </Stack>
);

/** 対応中の件数帯の列。受付/取得中の2つ。 */
const COUNT_ITEMS = [
  { status: "RECEIVED", label: "受付" },
  { status: "IN_PROGRESS", label: "取得中" },
] as const;

/**
 * 対応中の件数帯。白地と1px の枠のカード1枚を2列に区切り、ラベル（13px）と件数（28px）を縦に置く。
 * 件数が 0 の列も同じ大きさで出し、数字の色を text.secondary にする。
 */
const CountBand: React.FC<{ statuses: string[] }> = ({ statuses }) => {
  const theme = useTheme();
  // 受付・取得中以外の未知のstatusは「受付」に数える
  const countOf = (status: string): number =>
    statuses.filter((s) =>
      status === "RECEIVED" ? s !== "IN_PROGRESS" : s === status
    ).length;
  return (
    <Box
      component="ul"
      aria-label="対応中のご依頼の件数"
      sx={{
        ...sectionSx(theme),
        m: 0,
        p: 0,
        listStyle: "none",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        maxWidth: { [MOBILE_BREAKPOINT]: 480 },
      }}
    >
      {COUNT_ITEMS.map(({ status, label }, index) => {
        const n = countOf(status);
        return (
          <Box
            key={status}
            component="li"
            aria-label={`${label}${n}件`}
            sx={{
              minHeight: 88,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: { xs: 2, [MOBILE_BREAKPOINT]: 3 },
              borderLeft:
                index > 0 ? `1px solid ${theme.palette.border.main}` : 0,
            }}
          >
            <Typography
              component="span"
              aria-hidden
              sx={{ ...TEXT.meta, color: "text.secondary" }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              aria-hidden
              sx={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.3,
                fontVariantNumeric: "tabular-nums",
                color: n > 0 ? "text.primary" : "text.secondary",
              }}
            >
              {n}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/** 請求の注記。bluegray の地、枠なし、角丸4、13px。 */
const BillingNote: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      role="note"
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        px: 2,
        py: 1.5,
        mb: 2,
        backgroundColor: theme.palette.bluegray.main,
        borderRadius: "4px",
      }}
    >
      <InfoOutlined
        aria-hidden
        sx={{ fontSize: 16, mt: "3px", flexShrink: 0, color: "text.secondary" }}
      />
      <Box>
        <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
          納品前の金額は暫定です。取得した役所や書類によって増減することがあり、納品時に確定額に変わります。依頼の請求は納品日の月に、黒塗り資料の購入は購入日の月に計上します。
        </Typography>
        {/* 税区分は暫定の注意の文に混ぜず、別の行で示す */}
        <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
          表示している金額は税込です。消費税（10%）は取得手数料と黒塗り資料の購入にかかり、取得実費は非課税です。
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * R.E.ASSIST（役所資料の取得代行）の詳細。
 * 依頼状況・過去の依頼・請求を表示する。専用ページ（/reassist）で使う。
 */
export const ReassistDetail: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const archiveEnabled = useReassistArchiveEnabled();
  const requestsState = useReassistRequests();
  const billingState = useReassistBilling();
  const requests = requestsState.data;
  const billing = billingState.data;
  const requestsFailed =
    requestsState.error !== undefined && requests === undefined;
  const billingFailed =
    billingState.error !== undefined && billing === undefined;
  const [view, setView] = useRequestListView();
  // 入力中の検索語（draft）と、一覧に適用中の検索語（query）。検索条件は親で持つ
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");

  const all = requests?.results ?? [];
  const isDelivered = (r: ReassistRequestSummary): boolean =>
    r.status === "DELIVERED";
  const inProgressAll = all.filter((r) => !isDelivered(r));
  const searching = query !== "";
  const matched = all.filter((r) => matchesQuery(r, query));
  const inProgress = matched.filter((r) => !isDelivered(r));
  const delivered = matched.filter(isDelivered);
  const startDate = requests?.recordsStartDate;

  const applySearch = (): void => {
    setQuery(draft.trim());
  };
  const clearSearch = (): void => {
    setDraft("");
    setQuery("");
  };
  const goRequest = (): void => {
    navigate(REASSIST_REQUEST_PATH);
  };
  const goArchive = (): void => {
    navigate(REASSIST_ARCHIVE_PATH);
  };

  const renderList = (
    list: ReassistRequestSummary[],
    title: string
  ): React.ReactElement =>
    view === "table" ? (
      <RequestTable title={title} requests={list} />
    ) : (
      <Stack spacing={1.5}>
        {list.map((r) => (
          <ReassistRequestRow key={r.requestId} request={r} />
        ))}
      </Stack>
    );

  const noMatch = searching && matched.length === 0;

  // PC は見出しの右に「探す」（副）と「依頼する」（主）。SP は説明の下に「依頼する」を幅いっぱい、その下に「探す」を text で置く
  const headerActions = isMobile ? (
    <Stack gap={0.5}>
      <ReassistRequestButton onClick={goRequest} fullWidth />
      {archiveEnabled ? (
        <ReassistArchiveButton onClick={goArchive} variant="text" />
      ) : null}
    </Stack>
  ) : (
    <Stack direction="row" gap={1}>
      {archiveEnabled ? <ReassistArchiveButton onClick={goArchive} /> : null}
      <ReassistRequestButton onClick={goRequest} />
    </Stack>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: REASSIST_CONTENT_MAX_WIDTH,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ mb: { xs: 3, [MOBILE_BREAKPOINT]: 4 } }}>
        <ReassistPageHeader
          title="依頼状況・請求"
          description="依頼した役所資料の進み具合と、月ごとの請求額を確認できます。"
          actions={headerActions}
        />
      </Box>

      <Stack spacing={{ xs: 4, [MOBILE_BREAKPOINT]: 5 }}>
        {all.length > 0 && (
          <Stack spacing={2}>
            {inProgressAll.length > 0 && (
              <CountBand statuses={inProgressAll.map((r) => r.status)} />
            )}
            <ReassistToolbar
              draft={draft}
              onDraftChange={setDraft}
              onSearch={applySearch}
              onClear={clearSearch}
              searching={searching}
              view={view}
              onViewChange={setView}
              total={all.length}
              matched={matched.length}
            />
          </Stack>
        )}

        {noMatch && (
          <EmptyBlock
            icon={SearchOffOutlined}
            title="条件に合うご依頼はありません"
            description="検索語を変えて、もう一度お試しください。"
            action={
              <ReassistButton
                label="条件をクリア"
                variant="outlined"
                onClick={clearSearch}
              />
            }
          />
        )}

        {/* 検索で0件になったセクションは、見出しごと隠す */}
        {!(searching && inProgress.length === 0) && (
          <Box component="section" aria-label="対応中">
            <SectionTitle
              count={requests === undefined ? undefined : inProgress.length}
            >
              対応中
            </SectionTitle>
            {requestsFailed ? (
              <LoadFailed
                message={REQUESTS_FAILED}
                onRetry={requestsState.retry}
              />
            ) : requests === undefined ? (
              <LoadingCards
                rows={2}
                height={{ xs: 168, [MOBILE_BREAKPOINT]: 120 }}
              />
            ) : inProgress.length === 0 ? (
              <EmptyBlock
                icon={DescriptionOutlined}
                title="対応中のご依頼はありません"
                description="役所資料を依頼すると、ここに進み具合が表示されます。"
                // 見出しに主ボタンがあるため、ここは副ボタンにして contained を1画面1つにする
                action={
                  <ReassistRequestButton
                    onClick={goRequest}
                    variant="outlined"
                  />
                }
              />
            ) : (
              renderList(inProgress, "依頼一覧（対応中）")
            )}
          </Box>
        )}

        {!(searching && delivered.length === 0) && (
          <Box component="section" aria-label="納品済み">
            <SectionTitle
              count={requests === undefined ? undefined : delivered.length}
            >
              納品済み
            </SectionTitle>
            {requestsFailed ? (
              <LoadFailed
                message={REQUESTS_FAILED}
                onRetry={requestsState.retry}
              />
            ) : requests === undefined ? (
              <LoadingCards
                rows={2}
                height={{ xs: 168, [MOBILE_BREAKPOINT]: 120 }}
              />
            ) : delivered.length === 0 ? (
              <Typography sx={{ ...TEXT.body, color: "text.secondary" }}>
                納品済みのご依頼はまだありません
              </Typography>
            ) : (
              renderList(delivered, "依頼一覧（納品済み）")
            )}
          </Box>
        )}

        {!searching && (
          <Box component="section" aria-label="購入した役所資料">
            <SectionTitle>購入した役所資料</SectionTitle>
            <PurchasedDocumentsSection />
          </Box>
        )}

        <Box component="section" aria-label="請求">
          <SectionTitle>請求</SectionTitle>
          <BillingNote />
          {billingFailed ? (
            <LoadFailed
              message="請求を読み込めませんでした。少し待ってから再読み込みしてください。"
              onRetry={billingState.retry}
            />
          ) : billing === undefined ? (
            <LoadingCards
              rows={1}
              height={{ xs: 168, [MOBILE_BREAKPOINT]: 200 }}
              radius="4px"
            />
          ) : (
            <ReassistBillingTable rows={billing.results} />
          )}
        </Box>

        {startDate !== undefined && (
          <Typography
            sx={{
              ...TEXT.meta,
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {`${formatDate(
              startDate
            )} より前のご依頼は、記録がないため表示されません。`}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
